import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle2, XCircle, AlertCircle, Upload, RefreshCw } from 'lucide-react';
import { insertMissingQuizzes } from '@/scripts/insertMissingQuizzes';
import { getAvailableModuleIds } from '@/data/quizContent/quizRegistry';

export const QuizCoverageReport = () => {
  const [loading, setLoading] = useState(false);
  const [inserting, setInserting] = useState(false);
  const [stats, setStats] = useState<{
    totalModules: number;
    modulesWithQuizzes: number;
    coverage: number;
    availableGenerators: number;
  } | null>(null);
  const { toast } = useToast();

  const loadStats = async () => {
    setLoading(true);
    try {
      // Get total modules
      const { count: totalModules, error: moduleError } = await supabase
        .from('course_modules')
        .select('*', { count: 'exact', head: true })
        .eq('is_enabled', true);

      if (moduleError) throw moduleError;

      // Get modules with quizzes
      const { count: modulesWithQuizzes, error: quizError } = await supabase
        .from('module_quizzes')
        .select('*', { count: 'exact', head: true });

      if (quizError) throw quizError;

      const coverage = totalModules ? (modulesWithQuizzes! / totalModules) * 100 : 0;
      const availableGenerators = getAvailableModuleIds().length;

      setStats({
        totalModules: totalModules || 0,
        modulesWithQuizzes: modulesWithQuizzes || 0,
        coverage,
        availableGenerators
      });
    } catch (error: any) {
      toast({
        title: 'Error loading stats',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInsertQuizzes = async () => {
    setInserting(true);
    try {
      const results = await insertMissingQuizzes(false);
      
      toast({
        title: 'Quiz insertion complete',
        description: `Successfully inserted ${results.success.length} quizzes. Failed: ${results.failed.length}, Skipped: ${results.skipped.length}`,
        variant: results.failed.length > 0 ? 'destructive' : 'default'
      });

      // Reload stats
      await loadStats();
    } catch (error: any) {
      toast({
        title: 'Quiz insertion failed',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setInserting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Quiz Coverage Report</CardTitle>
            <CardDescription>
              Monitor quiz availability across all course modules
            </CardDescription>
          </div>
          <Button onClick={loadStats} disabled={loading} variant="outline" size="sm">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {stats && (
          <>
            {/* Coverage Overview */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Quiz Coverage</span>
                <span className="font-medium">{stats.coverage.toFixed(1)}%</span>
              </div>
              <Progress value={stats.coverage} className="h-2" />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{stats.modulesWithQuizzes} of {stats.totalModules} modules</span>
                <span>{stats.totalModules - stats.modulesWithQuizzes} missing</span>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">With Quizzes</span>
                </div>
                <p className="text-2xl font-bold">{stats.modulesWithQuizzes}</p>
              </div>

              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-1">
                  <XCircle className="h-4 w-4 text-destructive" />
                  <span className="text-sm font-medium">Missing</span>
                </div>
                <p className="text-2xl font-bold">{stats.totalModules - stats.modulesWithQuizzes}</p>
              </div>

              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium">Ready to Insert</span>
                </div>
                <p className="text-2xl font-bold">{stats.availableGenerators}</p>
              </div>
            </div>

            {/* Status Badge */}
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <p className="font-medium">Quiz Generation Status</p>
                <p className="text-sm text-muted-foreground">
                  {stats.availableGenerators} quiz generators available
                </p>
              </div>
              <Badge variant={stats.coverage === 100 ? 'default' : 'secondary'}>
                {stats.coverage === 100 ? '100% Complete' : 'In Progress'}
              </Badge>
            </div>

            {/* Action Button */}
            {stats.availableGenerators > 0 && stats.coverage < 100 && (
              <Button 
                onClick={handleInsertQuizzes} 
                disabled={inserting}
                className="w-full"
              >
                <Upload className={`h-4 w-4 mr-2 ${inserting ? 'animate-pulse' : ''}`} />
                {inserting ? 'Inserting Quizzes...' : `Insert ${stats.availableGenerators} Missing Quizzes`}
              </Button>
            )}
          </>
        )}

        {!stats && !loading && (
          <div className="text-center py-8 text-muted-foreground">
            <p>Click "Refresh" to load quiz coverage statistics</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
